import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface Integration {
  Id: string;
  ExternalId: string;
  IntegrationName: string;
  ExternalAuthentication: ExternalAuthentication;
  CustomData: Record<string, any>;
  CategoryId?: string;
  Deleted?: boolean;
}

export interface NewIntegrationResponse {
  IntegrationId: string;
  ApiKey: string; // Base 64 encoded
}

export interface ExternalAuthentication {
  ApiToken: string;
  SigningSecret: string;
}

export interface FlowExecutionLog {
  Id: string;
  ProcessId: string;
  ExternalId: string;
  IntegrationId: string;
  CreatedAt: string;
  StepsTotal: number;
  State: FlowExecutionState;
  CurrentStepName: string;
  StepsCompleted: number;
  Output: Map<string, any>;
}

export enum FlowExecutionState {
  Created = 'Created',
  Running = 'Running',
  Completed = 'Completed',
  Failed = 'Failed',
  Canceled = 'Canceled'
}

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  private api: AxiosInstance;
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor to dynamically set baseURL and include OAuth token
    this.api.interceptors.request.use((config) => {
      // Dynamically construct baseURL on each request to get current tenantId
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async getIntegrations(): Promise<Array<Integration> | null> {
    try {
      const response = await this.api.get('/integration');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getIntegration(id: string): Promise<Integration | null> {
    try {
      const response = await this.api.get('/integration/' + id);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateIntegration(integration: Integration): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.put('/integration/' + integration.Id, integration);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createIntegration(integration: Integration): Promise<NewIntegrationResponse | null> {
    try {
      const response = await this.api.post('/integration', integration);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteIntegration(id: string): Promise<AxiosResponse | null> {
    try {
      const response = await this.api.delete('/integration/' + id);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async restoreIntegration(id: string): Promise<AxiosResponse | null> {
    try {
      // Assumes restore is exposed through PATCH until a dedicated restore endpoint is available.
      const response = await this.api.patch('/integration/' + id, { Deleted: false });
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async executeFlow(externalId: string | null, inputData: Map<string, any>): Promise<FlowExecutionLog | null> {
    try {
      let endpoint = '/flow/execute';
      if (externalId) {
        endpoint += '/' + externalId;
      }

      const response = await this.api.post(endpoint, { InputData: inputData });
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
